import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '../../auth/services/auth.service';
import { UserRole } from '../models/user-role.enum';
import { HttpService } from './http.service';
import { MediaConsentDialogComponent } from '../components/dialog/media-consent-dialog/media-consent-dialog.component';
import { StudentWelcomeDialogComponent } from '../components/dialog/student-welcome-modal/student-welcome-dialog.component';

@Injectable({ providedIn: 'root' })
export class StudentPostLoginFlowService {
  private handledToken: string | null = null;
  private flowInProgress = false;

  constructor(
    private authService: AuthService,
    private dialog: MatDialog,
    private httpService: HttpService,
  ) {}

  runIfNeeded(): void {
    const token = this.authService.getToken();
    if (!token || this.flowInProgress || this.handledToken === token) {
      return;
    }

    if (this.authService.getCurrentUserRole() !== UserRole.Student) {
      this.handledToken = token;
      return;
    }

    const userDetails = this.getUserDetails();
    const userId = userDetails?.userId;
    if (!userId) {
      return;
    }

    this.flowInProgress = true;

    const continueFlow = () => this.checkAndShowMediaConsent(userId, token);
    const isFirstLogin = localStorage.getItem('studentIsFirstLogin') === 'true';

    if (isFirstLogin) {
      localStorage.setItem('studentIsFirstLogin', 'false');
      const welcomeRef = this.dialog.open(StudentWelcomeDialogComponent, {
        width: '560px',
        maxWidth: '95vw',
        maxHeight: '95vh',
        disableClose: true,
        data: { label: 'Student Portal!' },
      });

      welcomeRef.afterClosed().subscribe(() => continueFlow());
      return;
    }

    continueFlow();
  }

  private checkAndShowMediaConsent(userId: string, token: string): void {
    this.httpService
      .get<any>(`students/${userId}/media-consent/eligibility`)
      .subscribe({
        next: (res: any) => {
          const data = res?.data ?? res;
          const rawStatus = data?.mediaConsentStatus ?? '';
          const normalizedStatus = String(rawStatus).trim().toLowerCase();
          const dob: string = data?.dateOfBirth ?? '';

          if (!dob) {
            this.completeFlow(token);
            return;
          }

          const birthDate = new Date(dob);
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < birthDate.getDate())
          ) {
            age--;
          }

          const shouldShowMediaConsent =
            age >= 16 &&
            (normalizedStatus === '' || normalizedStatus === 'pending');

          if (!shouldShowMediaConsent) {
            this.completeFlow(token);
            return;
          }

          const consentRef = this.dialog.open(MediaConsentDialogComponent, {
            width: '580px',
            maxWidth: '95vw',
            maxHeight: '95vh',
            disableClose: true,
            panelClass: 'media-consent-dialog-panel',
            data: { userId },
          });

          consentRef.afterClosed().subscribe(() => this.completeFlow(token));
        },
        error: () => this.completeFlow(token),
      });
  }

  private completeFlow(token: string): void {
    this.handledToken = token;
    this.flowInProgress = false;
  }

  private getUserDetails(): { userId?: string } | null {
    try {
      const userDetails = localStorage.getItem('userDetails');
      return userDetails ? JSON.parse(userDetails) : null;
    } catch {
      return null;
    }
  }
}
